import "dayjs/locale/id";
import { Button, Flex } from "antd";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { now, tzDate } from "shared/utils/dayjs";
import { ScheduleTable } from "./ScheduleTable";
import { useGetInfiniteSchedules } from "./hook";
import { ClassCategory } from "shared/types/enum";
import type { ScheduleFilters } from "pages/schedule/filter/ScheduleFilterContext";
import { translate } from "shared/languages";
import PageHeading from "components/PageHeading.component";
import { CalendarSchedule } from "components/WeeklyCalendar/components/types";
import { Dayjs } from "dayjs";
import { ScheduleListFilter } from "./ScheduleListFilter";
import { InstructorColorProvider } from "context/InstructorColorProvider";
import { ScheduleViewToggle } from "pages/schedule/component/ScheduleViewToggle";
import { ScheduleDraftsButton } from "pages/schedule/component/ScheduleDraftsDrawer";
import BookingRefSearch from "components/bookings/BookingRefSearch.component";
import { getAllCustomerByBusinessId } from "hooks/queries/customer";

const DAY_FORMAT = "YYYY-MM-DD";

const generateTzDateRange = (start: Dayjs, end: Dayjs): string[] => {
  const days = end.diff(start, "day") + 1;

  return Array.from({ length: days }, (_, i) =>
    start.add(i, "day").format(DAY_FORMAT)
  );
};

const DEFAULT_FILTERS: ScheduleFilters = {
  instructorIds: [],
  customerIds: [],
  classIds: [],
  // Both types — the Private/Open toggle was removed; the list shows all
  // classes merged and distinguishes type via the Jenis Kelas column.
  classTypes: [ClassCategory.Private, ClassCategory.Open],
};

// Where the admin was in the list, kept on the list's own history entry so
// Back from Detail Jadwal (or anywhere else) lands on the same day at the same
// place, with the same filters and the same cached weeks. `day` is the day at
// the top of the list and `offset` how far into it (layout px).
type ListView = {
  day: string;
  offset: number;
  start: string;
  filters: ScheduleFilters;
};

// Written straight into the entry's state: a navigate({ replace }) would
// re-render the page on every scroll. React Router keeps the app's state under
// `usr` and reads it back as location.state on Back.
const writeListView = (listView: ListView) => {
  try {
    const entry = window.history.state ?? {};
    window.history.replaceState(
      { ...entry, usr: { ...(entry.usr ?? {}), listView } },
      ""
    );
  } catch {
    // Some browsers throttle replaceState; the next write catches up.
  }
};

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

// The distance from the container's top to an element's, in the container's
// scroll (layout) px. Rects are visual px inside the body zoom, so divide.
const offsetInContainer = (el: HTMLElement, container: HTMLElement) => {
  const box = container.getBoundingClientRect();
  const scale = container.offsetHeight
    ? box.height / container.offsetHeight
    : 1;
  return (el.getBoundingClientRect().top - box.top) / (scale || 1);
};

type Anchor = { day: string; offset: number; seq: number; smooth: boolean };

export const ScheduleList: React.FC = () => {
  const location = useLocation();
  // Read once: the entry's saved view on Back or refresh, none on a fresh visit.
  const [restored] = useState<ListView | undefined>(
    () => (location.state as { listView?: ListView } | null)?.listView
  );
  const todayKey = useMemo(() => now().format(DAY_FORMAT), []);
  const thisWeek = useMemo(
    () => now().startOf("week").toDate().toISOString(),
    []
  );

  const [filters, setFilters] = useState<ScheduleFilters>(
    restored?.filters ?? DEFAULT_FILTERS
  );
  const [startWeek] = useState(restored?.start ?? thisWeek);
  // The day the list scrolls to: the saved one, today, or today again when
  // Hari Ini is pressed (seq makes each press a new scroll).
  const [anchor, setAnchor] = useState<Anchor>(() => ({
    day: restored?.day ?? todayKey,
    offset: restored?.offset ?? 0,
    seq: 0,
    smooth: false,
  }));
  const settledSeqRef = useRef(-1);

  const { customerIds } = filters;

  // The filtered clients' names, highlighted in each row's Klien cell. The
  // same query the client filter's options come from (cached once).
  const { data: customers } = getAllCustomerByBusinessId({ is_active: true });
  const highlightNames = useMemo(() => {
    if (!customerIds.length || !customers) return new Set<string>();
    const ids = new Set(customerIds);
    return new Set(
      customers.filter((c) => ids.has(c.id)).map((c) => c.full_name)
    );
  }, [customerIds, customers]);

  // SCROLLING REFS
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const topRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const lastScrollTopRef = useRef(0);
  const isScrollingUpRef = useRef(false);
  const isLoadingAboveRef = useRef(false);

  const {
    data,
    fetchNextPage,
    isFetchingNextPage,
    fetchPreviousPage,
    isFetchingPreviousPage,
  } = useGetInfiniteSchedules(startWeek, filters);

  const { startRange, endRange } = useMemo(() => {
    const length = data?.pageParams.length;
    // Until the first page arrives, show the start week's empty days.
    if (!length) {
      return {
        startRange: tzDate(startWeek).startOf("week"),
        endRange: tzDate(startWeek).endOf("week"),
      };
    }

    const startRange = tzDate(data?.pageParams[0] as string).startOf("week");
    const endRange = tzDate(data?.pageParams[length - 1] as string).endOf(
      "week"
    );

    return { startRange, endRange };
  }, [data, startWeek]);

  const dates = useMemo(
    () => generateTzDateRange(tzDate(startRange), tzDate(endRange)),
    [startRange, endRange]
  );

  // Every loaded class by its Jakarta day, derived in render from the pages
  // themselves, so a cached list on Back shows in its first frame.
  const schedulesByDate = useMemo(() => {
    const byDate = new Map<string, CalendarSchedule[]>();
    const seen = new Set<string>();
    for (const page of data?.pages ?? []) {
      for (const schedule of page.schedules) {
        const start = tzDate(schedule.start_time);
        const key = `${schedule.schedule_id}-${start.toISOString()}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const day = start.format(DAY_FORMAT);
        const list = byDate.get(day);
        if (list) list.push(schedule);
        else byDate.set(day, [schedule]);
      }
    }
    return byDate;
  }, [data]);

  // Scroll to the anchor once its day is on the page. Settles only when there
  // is a screen's worth of list below it to land on; until then the loader
  // below fetches more weeks and this runs again.
  useLayoutEffect(() => {
    if (settledSeqRef.current === anchor.seq) return;
    if (!data?.pages?.length) return;
    const container = scrollContainerRef.current;
    const anchorEl = anchorRef.current;
    if (!container || !anchorEl) return;

    const target = Math.max(
      0,
      container.scrollTop +
        offsetInContainer(anchorEl, container) +
        anchor.offset
    );
    container.scrollTo({
      top: target,
      behavior: anchor.smooth && !prefersReducedMotion() ? "smooth" : "auto",
    });
    if (container.scrollHeight - target >= container.clientHeight) {
      settledSeqRef.current = anchor.seq;
    }
  }, [data, schedulesByDate, anchor]);

  // The day at the top of the list and how far into it the list is scrolled.
  const topOfList = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;
    for (const block of Array.from(
      container.querySelectorAll<HTMLElement>("[data-day]")
    )) {
      const top = offsetInContainer(block, container);
      if (top + block.offsetHeight > 0) {
        return { day: block.dataset.day as string, offset: Math.round(-top) };
      }
    }
    return undefined;
  }, []);

  // Save where the admin is: on a pointer down anywhere in the list (before
  // a click navigates away, pop-ups included) and shortly after scrolling.
  // Not while a scroll to the anchor is still in progress.
  const recordView = useCallback(() => {
    if (settledSeqRef.current !== anchor.seq) return;
    const top = topOfList();
    if (top) writeListView({ ...top, start: startWeek, filters });
  }, [anchor.seq, startWeek, filters, topOfList]);

  // A filter change reloads the list from its start week: stay on the day
  // that was at the top instead of wherever the shorter list leaves the
  // scroll. While the list is still on its way to a day (a saved one on
  // Back), keep going there.
  const changeFilters = useCallback<
    React.Dispatch<React.SetStateAction<ScheduleFilters>>
  >(
    (next) => {
      const top = topOfList();
      setFilters(next);
      setAnchor((prev) =>
        settledSeqRef.current !== prev.seq || !top
          ? { ...prev, seq: prev.seq + 1 }
          : { day: top.day, offset: 0, seq: prev.seq + 1, smooth: false }
      );
    },
    [topOfList]
  );
  const recordViewRef = useRef(recordView);
  useEffect(() => {
    recordViewRef.current = recordView;
    recordView();
  }, [recordView]);

  // Track scroll direction so the IO callback can distinguish "user reached
  // top while scrolling up" (load older) from "user landed at top after the
  // initial render" (don't load older).
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    let recordTimer: ReturnType<typeof setTimeout> | undefined;

    const onScroll = () => {
      const current = el.scrollTop;
      isScrollingUpRef.current = current < lastScrollTopRef.current;
      lastScrollTopRef.current = current;
      clearTimeout(recordTimer);
      recordTimer = setTimeout(() => recordViewRef.current(), 200);
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(recordTimer);
      el.removeEventListener("scroll", onScroll);
    };
  }, []);

  const loadAbove = useCallback(async () => {
    if (isLoadingAboveRef.current || isFetchingPreviousPage) return;
    isLoadingAboveRef.current = true;
    try {
      await fetchPreviousPage();
    } finally {
      isLoadingAboveRef.current = false;
    }
  }, [fetchPreviousPage, isFetchingPreviousPage]);

  const loadBelow = useCallback(async () => {
    if (isFetchingNextPage) return;
    await fetchNextPage();
  }, [fetchNextPage, isFetchingNextPage]);

  // Keep the latest loaders accessible from the IO callback without
  // recreating the observer on every fetch-state flip. Recreating the IO
  // re-fires for any sentinel still inside the rootMargin, which during
  // the empty/short initial state stacks up redundant fetchNextPage calls
  // (and thus redundant getNext/PreviousPageParam computations).
  const loadAboveRef = useRef(loadAbove);
  const loadBelowRef = useRef(loadBelow);
  useEffect(() => {
    loadAboveRef.current = loadAbove;
    loadBelowRef.current = loadBelow;
  }, [loadAbove, loadBelow]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !topRef.current || !bottomRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (entry.target === topRef.current && isScrollingUpRef.current) {
            loadAboveRef.current();
          }
          if (entry.target === bottomRef.current && !isScrollingUpRef.current) {
            loadBelowRef.current();
          }
        });
      },
      { root: container, rootMargin: "100px" }
    );

    observer.observe(topRef.current);
    observer.observe(bottomRef.current);

    return () => observer.disconnect();
  }, []);

  // Fetch the weeks the anchor needs: its own week when it isn't loaded yet
  // (a saved day whose weeks were dropped from the cache), then one more week
  // per commit until the area below it is at least a viewport tall (empty
  // weeks make short day blocks). Each fetched page re-renders, the effect
  // re-runs, and the loop converges.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (!data?.pages?.length) return;
    if (isFetchingNextPage || isFetchingPreviousPage) return;

    // 50ms lag lets AntD's async layout (Header / Flex / Table resize
    // observers) settle before we measure. With just a single rAF the
    // measurement fires too early and reports a tiny scrollHeight, so
    // needsMore comes back false and autofill silently no-ops.
    const timer = setTimeout(() => {
      if (settledSeqRef.current !== anchor.seq) {
        if (anchor.day > dates[dates.length - 1]) {
          fetchNextPage();
          return;
        }
        if (anchor.day < dates[0]) {
          fetchPreviousPage();
          return;
        }
      }
      const anchorEl = anchorRef.current;
      let needsMore: boolean;
      if (anchorEl) {
        const anchorContentOffset =
          container.scrollTop + offsetInContainer(anchorEl, container);
        needsMore =
          container.scrollHeight - anchorContentOffset <
          container.clientHeight + 1;
      } else {
        needsMore = container.scrollHeight <= container.clientHeight + 40;
      }
      if (needsMore) fetchNextPage();
    }, 50);

    return () => clearTimeout(timer);
  }, [
    data?.pages?.length,
    schedulesByDate,
    dates,
    anchor,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  ]);

  // Today's day block at the top of the list, scrolling there (today read at
  // the press, so a tab left open overnight still finds it).
  const goToToday = () =>
    setAnchor((prev) => ({
      day: now().format(DAY_FORMAT),
      offset: 0,
      seq: prev.seq + 1,
      smooth: true,
    }));

  return (
    <InstructorColorProvider>
      <PageHeading
        title={translate("schedule.name")}
        extra={
          <Flex align="center" gap={12}>
            <BookingRefSearch />
            <ScheduleDraftsButton />
            <ScheduleViewToggle />
          </Flex>
        }
      />
      <Flex vertical gap={20}>
        <ScheduleListFilter
          filters={filters}
          setFilters={changeFilters}
          leading={
            // Same control as the calendar's week bar (CalendarHeader).
            <Button
              style={{ borderRadius: 8, flexShrink: 0 }}
              onClick={goToToday}
            >
              {translate("schedule.feature.today")}
            </Button>
          }
        />
        <div
          ref={scrollContainerRef}
          onPointerDownCapture={() => recordViewRef.current()}
          style={{
            height: "100vh",
            overflowY: "auto",
          }}
        >
          <div ref={topRef} />
          {dates.map((date) => (
            <div
              key={date}
              data-day={date}
              ref={date === anchor.day ? anchorRef : null}
              style={{ marginBottom: 30 }}
            >
              <ScheduleTable
                date={date}
                schedules={schedulesByDate.get(date) || []}
                highlightNames={highlightNames}
              />
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      </Flex>
    </InstructorColorProvider>
  );
};
